// Статусите на маса върху сцената (резервации, избор на маса): как изглежда
// плотът, номерът и надписът под него. Цветовете са семантичните токени
// `schema-*` в src/index.css (светла + тъмна стойност).

import { BLOCKS } from "./blockDepth";

/**
 * - `free` — свободна (като в редактора: бял плот, сив контур)
 * - `occupied` — заета: розово-червен плот, червен плътен контур
 * - `reserved` — резервирана: кремав плот, БЛЕД кехлибарен ПУНКТИР (фокусът е в текста)
 * - `fits` — побира гостите (режим избор): зелен контур
 * - `selected` — избрана (режим избор): оранжев контур + ореол
 * - `blocked` — заета в този час (режим избор): щриховано сиво, некликаема
 * - `dimmed` — приглушена (извън фокуса)
 */
export type TableTone =
  | "free"
  | "occupied"
  | "reserved"
  | "fits"
  | "selected"
  | "blocked"
  | "dimmed";

/** Декорацията на маса върху сцената. Всичко е по желание. */
export interface TableStatus {
  tone?: TableTone;
  /** Надпис под номера ВМЕСТО местата: „35 мин", „19:00", „Заета", „Избрана". */
  // `compactText` — по-кратък вариант („4:52 ч") за малка маса, където `text`
  // („4 ч 52 мин") не се събира в плота.
  badge?: { text: string; compactText?: string; icon?: "clock" | "check" };
  /** Чип над масата (с часовник): часът на следващата резервация, „21:45". */
  chip?: string;
  /** Червена точка „!" горе вдясно (закъсняла резервация). */
  alert?: boolean;
  /** Ореол около плота (подчертана от списъка / отворен поповър). */
  highlighted?: boolean;
  /** Некликаема (напр. `blocked`) — `onTableClick` не се вика. */
  disabled?: boolean;
}

// Плотът: фон + контур по статуса. Сянката (страната на блокчето, подът,
// ореолът) е отделно — тя зависи от завъртането: `plateRing` + `blockShadow`.
const PLATE_TONE: Record<TableTone, string> = {
  free: "bg-surface border-[1.5px] border-schema-table-edge",
  occupied: "bg-schema-occupied border-[1.5px] border-schema-occupied-edge",
  reserved: "bg-schema-reserved border-2 border-dashed border-schema-reserved-edge-soft",
  fits: "bg-surface border-2 border-schema-fits-edge",
  selected: "bg-brand-tint border-[2.5px] border-brand",
  blocked:
    "border-[1.5px] border-schema-blocked-edge bg-[repeating-linear-gradient(135deg,var(--color-schema-blocked)_0_5px,var(--color-schema-blocked-stripe)_5px_10px)]",
  dimmed: "bg-surface border-[1.5px] border-schema-table-edge",
};

// Избраният елемент в РЕДАКТОРА (с ъгловите маркери) и подчертаната свободна маса.
const BRAND_OUTLINED = "bg-surface border-[2.5px] border-brand";

// Ореолът около плота/обекта: избор (редактор, „Избрана") и подчертаване.
export const SELECTION_RING = "0 0 0 5px var(--color-schema-halo)";
const HIGHLIGHT_RING = "0 0 0 7px var(--color-schema-halo)";

/**
 * Класовете на плота: избор в редактора > статус; подчертаната свободна
 * маса взима оранжев контур, както в дизайна.
 */
export const plateClass = (tone: TableTone, highlighted: boolean, editorSelected: boolean) =>
  editorSelected || (highlighted && tone === "free") ? BRAND_OUTLINED : PLATE_TONE[tone];

/** Ореолът на плота като слой за `box-shadow` (null — няма). */
export const plateRing = (tone: TableTone, highlighted: boolean, editorSelected: boolean) => {
  if (editorSelected) return SELECTION_RING;
  if (highlighted) return HIGHLIGHT_RING;
  return tone === "selected" ? SELECTION_RING : null;
};

// Страната на блокчето: неутрална при всеки статус (както в дизайна), а
// „заета в този час" е приглушена — масата там е неактивна.
export const plateSideColor = (tone: TableTone) =>
  tone === "blocked" ? "var(--color-schema-blocked-edge)" : BLOCKS.table.side;

// Номерът на масата.
export const NAME_TONE: Record<TableTone, string> = {
  free: "text-ink",
  occupied: "text-ink",
  reserved: "text-ink",
  fits: "text-ink",
  selected: "text-ink",
  blocked: "text-schema-blocked-ink",
  dimmed: "text-ink",
};

// Надписът под номера (местата или `badge`).
export const BADGE_TONE: Record<TableTone, string> = {
  free: "text-body",
  occupied: "text-schema-occupied-ink",
  reserved: "text-schema-reserved-ink",
  fits: "text-schema-fits-ink",
  selected: "text-brand-deep-ink",
  blocked: "text-schema-blocked-ink",
  dimmed: "text-body",
};
